import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  DialogActions,
  IconButton,
} from "@mui/material";
import AddLead from "./AddLead";
import { url } from "../constants/url";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import LeadRemarks from "./LeadRemarks";
import AssignTeamMemberLead from "./AssignTeamMemberLead";
import { useNavigate } from "react-router-dom";
import {
  RemoveRedEyeRounded,
  ArrowBack,
  ArrowForward,
} from "@mui/icons-material";

const AllLeads = ({ userData }) => {
  const navigate = useNavigate();
  const UID = userData?._id;
  const CID = userData?.companyAdded[0]?.companyId;
  const [openAddLeadPop, setOpenAddLeadPop] = useState(false);
  const [leadsData, setLeadsData] = useState([]);

  // state for update status dialog
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState({
    callStatus: "",
    leadStatus: "",
    dateTime: "",
    meetingPlatform: "",
    meetingLink: "",
    location: "",
    remarks: "",
    meetingRemarks: "",
    remarksOnStatusChange: "",
  });
  const [viewRemarksOpen, setViewRemarksOpen] = useState(false);
  const [assignLeadOpen, setAssignLeadOpen] = useState(false);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0] // default today (YYYY-MM-DD)
  );

  const handleOpenAddLead = () => setOpenAddLeadPop(true);
  const handleCloseAddLead = () => setOpenAddLeadPop(false);

  const handleOpenAssignLead = () => setAssignLeadOpen(true);
  const handleCloseAssignLead = () => setAssignLeadOpen(false);

  const fetchLeadsAddedByMember = async () => {
    try {
      const response = await fetch(
        `${url}/api/leads/fetch-leads-added?companyId=${CID}`
      );
      const data = await response.json();

      const nonDeadLeads = (data.data || []).filter(
        (lead) => lead.status !== "dead"
      );
      const deadLeads = (data.data || []).filter(
        (lead) => lead.status === "dead"
      );

      setLeadsData([...nonDeadLeads, ...deadLeads]);
    } catch (error) {
      console.error("❌ Error fetching leads:", error);
    }
  };

  const fetchLeadsAssignedToMe = async () => {
    try {
      const response = await fetch(
        `${url}/api/leads/fetch-leads-assigned?teamMemberId=${UID}`
      );
      const data = await response.json();

      // Separate dead leads and non-dead leads
      const nonDeadLeads = (data.data || []).filter(
        (lead) => lead.status !== "dead"
      );
      const deadLeads = (data.data || []).filter(
        (lead) => lead.status === "dead"
      );

      // Merge: active first, then dead
      const sortedLeads = [...nonDeadLeads, ...deadLeads];

      setAssignedLeads(sortedLeads);
      console.log("Leads assigned fetched:", data.data);
    } catch (error) {
      console.error("❌ Error fetching leads:", error);
    }
  };
  useEffect(() => {
    if (userData?.role === "admin") {
      fetchLeadsAddedByMember();
    }
  }, [selectedDate, openAddLeadPop]);

  useEffect(() => {
    if (userData?.role === "admin") {
      fetchLeadsAddedByMember();
    } else if (userData?.role === "teamMember") {
      fetchLeadsAssignedToMe();
    }
  }, [openAddLeadPop]);

  // --- Update Lead Status Handlers ---
  const handleOpenUpdateDialog = (lead) => {
    setSelectedLead(lead);
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedLead(null);
    setFormData({
      callStatus: "",
      leadStatus: "", // For Classification
      schedulingAction: "", // For Scheduling
      dateTime: "",
      meetingPlatform: "",
      meetingLink: "",
      location: "",
      remarks: "",
      meetingRemarks: "",
      remarksOnStatusChange: "",
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpdateLeadStatus = async () => {
    try {
      console.log("Updating lead with data:", formData);
      const response = await fetch(`${url}/api/leads/update-lead-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: selectedLead._id,
          userId: UID,
          ...formData,
          // Explicitly map categorical fields for backend if needed
          classificationStatus: formData.leadStatus,
          schedulingStatus: formData.schedulingAction,
          dateTime: formData.dateTime || formData.meetingDateTime,
        }),
      });
      const data = await response.json();
      console.log("Lead updated:", data);
      await fetchLeadsAddedByMember(); // refresh table
      handleCloseUpdateDialog();
    } catch (error) {
      console.error("❌ Error updating lead:", error);
    }
  };

  const handleOpenViewRemarks = async () => {
    setViewRemarksOpen(true);
  };
  const handleCloseViewRemarks = () => {
    setViewRemarksOpen(false);
  };

  // Prepare data for charts
  const leadStatusCounts = leadsData.reduce((acc, lead) => {
    acc[lead.leadStatus] = (acc[lead.leadStatus] || 0) + 1;
    return acc;
  }, {});

  const priorityCounts = leadsData.reduce((acc, lead) => {
    acc[lead.priority] = (acc[lead.priority] || 0) + 1;
    return acc;
  }, {});

  const leadStatusData = Object.entries(leadStatusCounts).map(
    ([key, value]) => ({
      name: key,
      value,
    })
  );

  const priorityData = Object.entries(priorityCounts).map(([key, value]) => ({
    name: key,
    value,
  }));

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#AA336A",
    "#336AAA",
  ];

  return (
    <>
      <Box
        sx={(theme) => ({
          minHeight: "100vh",
          width: "100%",
          bgcolor: theme.palette.background.default,
          color: theme.palette.text.primary,
          p: 3,
        })}
      >
        <Typography variant="h4" gutterBottom>
          Leads
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage your leads here.
        </Typography>

        {/* Quick Actions */}
        <Card sx={{ mb: 3, bgcolor: "background.paper" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenAddLead}
            >
              Add New Lead
            </Button>
          </CardContent>
        </Card>

        <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
          <IconButton
            onClick={() => {
              const d = new Date(selectedDate);
              d.setDate(d.getDate() - 1);
              setSelectedDate(d.toISOString().split("T")[0]);
            }}
          >
            <ArrowBack />
          </IconButton>
          <Button
            variant="outlined"
            onClick={() => {
              const d = new Date();
              d.setDate(d.getDate() - 1);
              setSelectedDate(d.toISOString().split("T")[0]);
            }}
          >
            Yesterday
          </Button>

          <Button
            variant="outlined"
            onClick={() =>
              setSelectedDate(new Date().toISOString().split("T")[0])
            }
          >
            Today
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              const d = new Date();
              d.setDate(d.getDate() + 1);
              setSelectedDate(d.toISOString().split("T")[0]);
            }}
          >
            Tomorrow
          </Button>
          <IconButton
            onClick={() => {
              const d = new Date(selectedDate);
              d.setDate(d.getDate() + 1);
              setSelectedDate(d.toISOString().split("T")[0]);
            }}
          >
            <ArrowForward />
          </IconButton>
          <TextField
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="small"
          />
        </Box>

        {/* Leads Table */}
        {(
          userData?.role === "teamMember"
            ? assignedLeads.length === 0
            : leadsData.length === 0
        ) ? (
          <Typography>
            {userData?.role === "teamMember"
              ? "No leads assigned to you"
              : "No leads added"}
          </Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{ maxHeight: "70vh", overflow: "auto" }}
            >
              <Table stickyHeader>
                <TableHead sx={{ bgcolor: "background.paper" }}>
                  <TableRow>
                    <TableCell>Sr. No</TableCell>
                    <TableCell>Client Name</TableCell>

                    <TableCell>Contact No</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Lead Source</TableCell>
                    <TableCell>Service Interested</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Lead Status</TableCell>
                    {/* <TableCell>Priority</TableCell> */}
                    <TableCell>Call Status</TableCell>
                    <TableCell>No. of Calls</TableCell>
                    {/* <TableCell>Quotation Sent</TableCell> */}
                    <TableCell>Lead Call Date</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Actions</TableCell>
                    {userData?.role === "admin" && (
                      <TableCell>Assign</TableCell>
                    )}
                  </TableRow>
                </TableHead>

                {/* <TableRow
                key={lead._id}
                sx={{
                  bgcolor:
                    lead.status === "meeting_scheduled" &&
                    new Date(lead.meeting.dateTime).toDateString() ===
                      new Date().toDateString()
                      ? "warning.light"
                      : "inherit",
                }} 
              ></TableRow> */}

                <TableBody>
                  {(userData?.role === "teamMember"
                    ? assignedLeads
                    : leadsData
                  ).map((lead) => (
                    <TableRow
                      key={lead._id}
                      sx={(theme) => {
                        const classification = lead.classificationStatus || (["hot", "warm", "cold", "dead", "closed"].includes(lead.status) ? lead.status : null);
                        let bgcolor = "inherit";

                        if (classification === "hot" || classification === "warm") {
                          bgcolor = theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.2)" : "rgba(2, 136, 209, 0.1)";
                        } else if (classification === "dead") {
                          bgcolor = theme.palette.mode === "dark" ? "rgba(158, 158, 158, 0.2)" : "rgba(158, 158, 158, 0.1)";
                        } else if (classification === "closed") {
                          bgcolor = theme.palette.mode === "dark" ? "rgba(237, 108, 2, 0.2)" : "rgba(237, 108, 2, 0.1)";
                        } else if (lead.status === "meeting_scheduled" && new Date(lead.meeting?.dateTime).toDateString() === new Date().toDateString()) {
                          bgcolor = "warning.light";
                        }

                        return { bgcolor };
                      }}
                    >
                      <TableCell>{leadsData.indexOf(lead) + 1}</TableCell>
                      <TableCell>{lead.clientName}</TableCell>
                      <TableCell>{lead.contactNo}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>
                        {lead.leadSource?.source}
                        {lead.leadSource?.otherSource && (
                          <> ({lead.leadSource.otherSource})</>
                        )}
                        <br />
                        <small>{lead.leadSource?.details}</small>
                      </TableCell>
                      <TableCell>
                        {/* check if serviceinterested is "other" */}

                        {Array.isArray(lead.serviceInterested) &&
                          lead.serviceInterested.map((s) => (
                            <div key={s._id}>
                              {s.service === "Other"
                                ? s.otherService
                                : s.service}
                            </div>
                          ))}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {/* Classification Status */}
                          {(lead.classificationStatus && lead.classificationStatus !== 'none') || (["hot", "warm", "cold", "dead", "closed", "new", "follow-up"].includes(lead.status)) ? (
                            <div style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: lead.classificationStatus === 'hot' || lead.status === 'hot' ? '#1976d2' : '#9c27b0',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              {(lead.classificationStatus || lead.status).toUpperCase()}
                            </div>
                          ) : null}

                          {/* Scheduling Status */}
                          {lead.schedulingStatus && lead.schedulingStatus !== 'none' && (
                            <div style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#ed6c02',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              {lead.schedulingStatus.toUpperCase()}
                            </div>
                          )}

                          {/* Quotation Status */}
                          {lead.quotationStatus && lead.quotationStatus !== 'none' && (
                            <div style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#2e7d32',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}>
                              {lead.quotationStatus.toUpperCase()}
                            </div>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{lead.leadStatus}</TableCell>
                      {/* <TableCell>{lead.priority}</TableCell> */}
                      <TableCell>{lead.callStatus}</TableCell>
                      <TableCell>{lead.noOfTimesCalled}</TableCell>
                      {/* <TableCell>
                      Email: {lead.quotation?.sentToEmail ? "✅" : "❌"}
                      <br />
                      WhatsApp: {lead.quotation?.sentToWhatsApp ? "✅" : "❌"}
                    </TableCell> */}
                      <TableCell>
                        {lead.leadCallDate
                          ? new Date(lead.leadCallDate).toLocaleString()
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/lead/${lead._id}`)} // 👈 pass leadId in URL
                        >
                          Records
                        </Button>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenUpdateDialog(lead)}
                        >
                          Update Status
                        </Button>
                      </TableCell>
                      {userData?.role === "admin" && (
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              handleOpenAssignLead();
                              setSelectedLead(lead);
                            }}
                          >
                            Assign
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
      {/* Add Lead Popup */}
      <Dialog
        open={openAddLeadPop}
        onClose={handleCloseAddLead}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Lead</DialogTitle>
        <DialogContent sx={{ bgcolor: "background.default" }}>
          <AddLead userData={userData} onClose={handleCloseAddLead} />
        </DialogContent>
      </Dialog>
      {/* Update Lead Status Popup */}
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Lead Status</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            bgcolor: "background.default",
          }}
        >
          <TextField
            select
            fullWidth
            label="Call Status"
            name="callStatus"
            value={formData.callStatus}
            onChange={handleChange}
          >
            <MenuItem value="call_not_received">Call Not Received</MenuItem>
            <MenuItem value="received">Received</MenuItem>
            <MenuItem value="switch_off">Switch Off</MenuItem>
            <MenuItem value="wrong_number">Wrong Number</MenuItem>
          </TextField>

          {/* Show Lead Status only if call was received */}
          {formData.callStatus === "received" && (
            <TextField
              select
              fullWidth
              label="Classification"
              name="leadStatus"
              value={formData.leadStatus}
              onChange={handleChange}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="hot">Hot</MenuItem>
              <MenuItem value="warm">Warm</MenuItem>
              <MenuItem value="cold">Cold</MenuItem>
              <MenuItem value="dead">Dead</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </TextField>
          )}

          {/* Scheduling Actions */}
          {formData.callStatus && formData.callStatus !== "wrong_number" && (
            <TextField
              select
              fullWidth
              label="Scheduling Action"
              name="schedulingAction"
              value={formData.schedulingAction || ""}
              onChange={handleChange}
            >
              <MenuItem value="">No direct action</MenuItem>
              <MenuItem value="rescheduled">Rescheduled</MenuItem>
              {formData.callStatus === "received" && (
                <MenuItem value="meeting_scheduled">Meeting Scheduled</MenuItem>
              )}
            </TextField>
          )}

          {/* Show reschedule date/time if rescheduled or meeting scheduled */}
          {(formData.schedulingAction === "rescheduled" || formData.schedulingAction === "meeting_scheduled") && (
            <TextField
              fullWidth
              label={formData.schedulingAction === "rescheduled" ? "Reschedule Date/Time" : "Meeting Date/Time"}
              type="datetime-local"
              name="dateTime"
              value={formData.dateTime}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          )}

          {/* Show meeting fields if meeting scheduled */}
          {formData.schedulingAction === "meeting_scheduled" && (
            <>
              <TextField
                fullWidth
                select
                label="Meeting Platform"
                name="meetingPlatform"
                value={formData.meetingPlatform}
                onChange={handleChange}
              >
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="zoom">Zoom</MenuItem>
                <MenuItem value="google_meet">Google Meet</MenuItem>
                <MenuItem value="microsoft_teams">Microsoft Teams</MenuItem>
              </TextField>
              <TextField
                fullWidth
                label={
                  formData.meetingPlatform === "offline"
                    ? "Meeting Location"
                    : "Meeting Link"
                }
                name={
                  formData.meetingPlatform === "offline"
                    ? "location"
                    : "meetingLink"
                }
                value={
                  formData.meetingPlatform === "offline"
                    ? formData.location
                    : formData.meetingLink
                }
                onChange={handleChange}
              />
              <TextField
                fullWidth
                label="Meeting Date/Time"
                type="datetime-local"
                name="meetingDateTime" // <-- separate field
                value={formData.dateTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="Meeting Remarks"
                name="meetingRemarks"
                value={formData.meetingRemarks}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </>
          )}

          <TextField
            fullWidth
            label="Remarks"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            multiline
            rows={3}
          />

          {/* <TextField
            fullWidth
            label="Remarks on Status Change"
            name="remarksOnStatusChange"
            value={formData.remarksOnStatusChange}
            onChange={handleChange}
            multiline
            rows={2}
          /> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateLeadStatus}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {viewRemarksOpen && selectedLead && (
        <Dialog
          open={viewRemarksOpen}
          onClose={handleCloseViewRemarks}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>View Remarks</DialogTitle>
          <DialogContent sx={{ bgcolor: "background.default" }}>
            <LeadRemarks leadId={selectedLead._id} />
          </DialogContent>
        </Dialog>
      )}

      {assignLeadOpen && selectedLead && (
        <Dialog
          open={assignLeadOpen}
          onClose={handleCloseAssignLead}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Assign Lead</DialogTitle>
          <DialogContent sx={{ bgcolor: "background.default" }}>
            {/* Content for assigning lead goes here */}
            <AssignTeamMemberLead
              userData={userData}
              leadId={selectedLead._id}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignLead}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default AllLeads;
